
import React from 'react';
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarX } from "lucide-react";
import { EOLItem } from '@/types/dashboard';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';

interface EOLCardProps {
  eolItems: EOLItem[];
  isLoading: boolean;
}

export function EOLCard({ eolItems, isLoading }: EOLCardProps) {
  const { t, language } = useLanguage();
  const locale = language === "pl" ? pl : enUS;

  const getProductSlug = (name: string) => {
    const map: Record<string, string> = {
      'Fedora Linux': 'fedora',
      'Palo Alto Networks Cortex XDR agent': 'cortex-xdr', // Best guess, or maybe 'palo-alto-networks'
      'Amazon EKS': 'amazon-eks',
      'Red Hat Enterprise Linux': 'rhel',
      'Alpine Linux': 'alpine',
      'Oracle Linux': 'oracle-linux',
      'Rocky Linux': 'rocky-linux',
      'AlmaLinux OS': 'almalinux',
    };
    return map[name] || name.toLowerCase().replace(/\s+/g, '-');
  };

  return (
    <Card className="border-border/50 h-full bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 hover:border-primary/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center">
          <div className="p-2 rounded-lg bg-primary/10 mr-3 group-hover:bg-primary/20 transition-colors">
            <CalendarX className="h-5 w-5 text-primary" />
          </div>
          <Link to="/eol" className="hover:text-primary transition-colors flex-1">
            {t('dashboard.upcoming_eol')}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-5 w-3/4 bg-secondary/50 animate-pulse rounded-md" />
                <div className="h-4 w-1/4 bg-secondary/30 animate-pulse rounded-md" />
              </div>
            ))}
          </div>
        ) : eolItems && eolItems.length > 0 ? (
          <div className="space-y-4">
            {eolItems.map((item, index) => (
              <Link
                key={index}
                to={`/eol/${getProductSlug(item.name)}`}
                className="flex flex-col p-2 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <span className="font-medium group-hover:text-primary transition-colors">{item.name}</span>
                  <Badge variant="outline" className="ml-2">
                    {item.cycle}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground flex justify-between items-center mt-1">
                  <span>
                    {t('eol.eolDate')}: {item.eol ? format(new Date(item.eol), 'PP', { locale }) : '-'}
                  </span>
                  <span className="text-xs bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium">
                    {item.daysRemaining} {t('eol.daysRemaining')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('common.no_results')}</p>
        )}
      </CardContent>
    </Card>
  );
}
